import { useCallback }     from 'react';
import { Grid }            from '@mui/material';
import { useDropzone }     from 'react-dropzone';
import { 
  PickerBorder, 
  PickerDocumentContainer, 
  PickerFileSpecificationMessage, 
  PickerIconWrapper, 
  PickerLinkLikeText 
}                          from '@styles/InputStyles';
import { DocumentPreview } from './DocumentPreview';

interface IDocumentPickerProps {
  icon?: JSX.Element;
  title?: string;
  files: File[];
  multiple?: boolean;

  onChange(files: File[] | null): void;
}

export const DocumentPicker = ({
  icon,
  title = 'Drop your document here',
  files = [],
  multiple = false,
  onChange,
}: IDocumentPickerProps) => {
  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      if (acceptedFiles.length) {
        onChange(acceptedFiles);
      }
    },
    [files, onChange],
  );

  const fiveMB = 1024 * 1024 * 5;

  const { getRootProps, getInputProps, fileRejections } = useDropzone({
    onDrop,
    multiple,
    maxSize: fiveMB,
    accept: {
      'application/pdf': ['.pdf'],
    },
  });

  const onRemove = useCallback(
    (index: number) => {
      const nextFiles = [...files];
      nextFiles.splice(index, 1);

      onChange(nextFiles);
    },
    [files],
  );

  const acceptedFileItems = files?.map((file, index, array) => {
    const size = array.length > 2 ? 4 : array.length > 1 ? 6 : 12;

    return (
      <Grid key={index} item xs={12} sm={size}>
        <DocumentPreview key={index} file={file} onRemove={() => onRemove(index)} />
      </Grid>
    );
  });

  const fileRejectionItems = fileRejections.map(({ file, errors }, index) => (
    <li key={index}>
      {file.name} - {file.size} bytes
      <ul>
        {errors.map((e) => (
          <li key={e.code}>
            <PickerFileSpecificationMessage error>{e.message}</PickerFileSpecificationMessage>
          </li>
        ))}
      </ul>
    </li>
  ));

  return (
    <PickerDocumentContainer>
      {(!files || files?.length < 5) && (
        <PickerBorder {...getRootProps()}>
          <input {...getInputProps()} />

          {icon && <PickerIconWrapper>{icon}</PickerIconWrapper>}

          <div>
            {title} or <PickerLinkLikeText>browse</PickerLinkLikeText>
          </div>

          <ul>{fileRejectionItems}</ul>
        </PickerBorder>
      )}

      {!!acceptedFileItems.length && (
        <Grid container sx={{ flex: 1 }} spacing={1}>
          {acceptedFileItems}
        </Grid>
      )}
    </PickerDocumentContainer>
  );
};
